import React, { useState, useEffect } from 'react'
import {
  BookOpen,
  TrendingUp,
  Award,
  AlertCircle,
  Calendar,
  CheckCircle2
} from 'lucide-react'
import Card from '../../components/common/Card'
import { detalleNotaService } from '../../services/detalleNotaService'
import { periodoService } from '../../services/periodoService'
import { useAuth } from '../../contexts/AuthContext'
import toast from 'react-hot-toast'

const MisNotas = () => {
  const { user } = useAuth()
  const [periodos, setPeriodos] = useState([])
  const [selectedPeriodo, setSelectedPeriodo] = useState(null)
  const [notas, setNotas] = useState(null)
  const [loading, setLoading] = useState(false)

  // Obtener ID del estudiante desde el usuario autenticado
  const estudianteId = user?.persona_id || user?.id

  useEffect(() => {
    fetchPeriodos()
  }, [])

  const fetchPeriodos = async () => {
    try {
      const response = await periodoService.getPeriodosSimple()
      if (response.success) {
        setPeriodos(response.data || [])
        if (response.data?.length > 0) {
          setSelectedPeriodo(response.data[0].id)
        }
      } else {
        toast.error('Error al cargar períodos')
      }
    } catch (error) {
      toast.error('Error de conexión')
    }
  }

  const fetchNotas = async () => {
    if (!selectedPeriodo || !estudianteId) {
      toast.error('No se pudo identificar el estudiante o período')
      return
    }

    try {
      setLoading(true)
      const response = await detalleNotaService.getNotasEstudiante(estudianteId, selectedPeriodo)
      
      if (response.success) {
        setNotas(response.data)
      } else {
        toast.error(response.message || 'Error al cargar notas')
        setNotas(null)
      }
    } catch (error) {
      toast.error('Error de conexión')
      setNotas(null)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (selectedPeriodo) {
      fetchNotas()
    }
  }, [selectedPeriodo])

  const calcularPromedio = (evaluaciones) => {
    if (!evaluaciones || evaluaciones.length === 0) return 0
    const total = evaluaciones.reduce((sum, e) => sum + (e.nota || 0), 0)
    return (total / evaluaciones.length).toFixed(2)
  }

  const getEstadoEvaluacion = (nota) => {
    if (!nota) return { estado: 'Sin calificar', color: 'gray', icono: AlertCircle }
    if (nota >= 7) return { estado: 'Aprobado', color: 'success', icono: CheckCircle2 }
    return { estado: 'Reprobado', color: 'error', icono: AlertCircle }
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-accent-500 rounded-2xl flex items-center justify-center shadow-glow">
          <BookOpen className="h-6 w-6 text-white" />
        </div>
        <div>
          <h1 className="text-3xl font-bold gradient-text">Mis Notas</h1>
          <p className="text-gray-600 dark:text-gray-400">Visualiza tus calificaciones por período</p>
        </div>
      </div>

      {/* Selector de Período */}
      <Card className="gradient" shadow="glow-lg">
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Período Académico
            </label>
            <select
              value={selectedPeriodo || ''}
              onChange={(e) => setSelectedPeriodo(e.target.value)}
              className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 focus:ring-2 focus:ring-primary-500"
            >
              {periodos.map(p => (
                <option key={p.id} value={p.id}>{p.nombre}</option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {/* Contenido */}
      {loading ? (
        <Card className="gradient" shadow="glow-lg">
          <div className="text-center py-12">
            <div className="inline-block animate-spin">
              <div className="h-8 w-8 border-4 border-primary-200 border-t-primary-500 rounded-full" />
            </div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">Cargando notas...</p>
          </div>
        </Card>
      ) : notas ? (
        <div className="space-y-6">
          {/* Resumen */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="gradient" shadow="glow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-600 dark:text-gray-400">Promedio General</p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                    {calcularPromedio(notas.evaluaciones)}
                  </p>
                </div>
                <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center shadow-glow">
                  <TrendingUp className="h-6 w-6 text-white" />
                </div>
              </div>
            </Card>

            <Card className="gradient" shadow="glow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-600 dark:text-gray-400">Materias</p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                    {notas.materias?.length || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-gradient-to-br from-accent-500 to-accent-600 rounded-xl flex items-center justify-center shadow-glow">
                  <BookOpen className="h-6 w-6 text-white" />
                </div>
              </div>
            </Card>

            <Card className="gradient" shadow="glow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-600 dark:text-gray-400">Evaluaciones</p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                    {notas.evaluaciones?.length || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-gradient-to-br from-success-500 to-success-600 rounded-xl flex items-center justify-center shadow-glow">
                  <Award className="h-6 w-6 text-white" />
                </div>
              </div>
            </Card>
          </div>

          {/* Evaluaciones por Materia */}
          <div className="space-y-4">
            {notas.materias?.map(materia => {
              const evaluacionesMateria = notas.evaluaciones?.filter(e => e.materia_id === materia.id) || []
              const promediaMateria = calcularPromedio(evaluacionesMateria)
              const aprobadas = evaluacionesMateria.filter(e => e.nota && e.nota >= 7).length

              return (
                <Card key={materia.id} className="gradient" shadow="glow-lg">
                  <div className="space-y-4">
                    {/* Header de la Materia */}
                    <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 pb-4">
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                          {materia.nombre}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Código: {materia.codigo}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-primary-600">
                          {promediaMateria}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {aprobadas}/{evaluacionesMateria.length} aprobadas
                        </p>
                      </div>
                    </div>

                    {/* Evaluaciones de la Materia */}
                    <div className="space-y-3">
                      {evaluacionesMateria.length > 0 ? (
                        evaluacionesMateria.map(evaluacion => {
                          const estadoInfo = getEstadoEvaluacion(evaluacion.nota)
                          const IconoEstado = estadoInfo.icono
                          const colorMap = {
                            success: 'bg-success-100 text-success-800 dark:bg-success-900/20 dark:text-success-400',
                            error: 'bg-error-100 text-error-800 dark:bg-error-900/20 dark:text-error-400',
                            gray: 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'
                          }

                          return (
                            <div key={evaluacion.id} className="flex items-center justify-between p-3 rounded-xl bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                              <div className="flex-1">
                                <p className="font-medium text-gray-900 dark:text-gray-100">
                                  {evaluacion.nombre}
                                </p>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                  Porcentaje: {evaluacion.porcentaje}%
                                </p>
                              </div>

                              <div className="flex items-center space-x-4">
                                <div className={`px-3 py-1 rounded-full text-sm font-medium ${colorMap[estadoInfo.color]}`}>
                                  <div className="flex items-center space-x-2">
                                    <IconoEstado className="h-4 w-4" />
                                    <span>{evaluacion.nota ? evaluacion.nota : estadoInfo.estado}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )
                        })
                      ) : (
                        <p className="text-center text-gray-500 dark:text-gray-400 py-4">
                          No hay evaluaciones para esta materia
                        </p>
                      )}
                    </div>
                  </div>
                </Card>
              )
            })}
          </div>
        </div>
      ) : (
        <Card className="gradient" shadow="glow-lg">
          <div className="text-center py-12">
            <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">
              No hay notas registradas para este período
            </p>
          </div>
        </Card>
      )}
    </div>
  )
}

export default MisNotas
